import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import SimpleFadeIn from './SimpleFadeIn';

const FAQSection = styled.section`
  background: var(--color-primary-base);
  padding: var(--spacing-section-mobile) 0;
  
  @media (min-width: 768px) {
    padding: var(--spacing-section) 0;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--spacing-2xl);
`;

const FAQItem = styled.div`
  background: var(--white);
  border-radius: 8px;
  margin-bottom: var(--spacing-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const FAQQuestion = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: var(--spacing-lg);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--color-primary-light);
  }
  
  h3 {
    font-size: var(--fs-lg);
    color: var(--text-primary);
    font-weight: var(--font-weight-bold);
    margin: 0;
    flex: 1;
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--color-accent-primary);
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => $isOpen ? '500px' : '0'};
  opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
  transition: all 0.3s ease;
  overflow: hidden;
  
  p {
    padding: 0 var(--spacing-lg) var(--spacing-lg);
    color: var(--text-secondary);
    line-height: var(--lh-relaxed);
    margin: 0;
  }
  
  ul {
    padding: 0 var(--spacing-lg) var(--spacing-lg) var(--spacing-2xl);
    margin: 0;
    
    li {
      color: var(--text-secondary);
      margin-bottom: var(--spacing-xs);
    }
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--fs-lg);
  max-width: 600px;
  margin: 0 auto;
`;

const faqs = [
  {
    question: "Will AI replace our employees?",
    answer: "No - we're doing something far more powerful. Any business automation can handle repetitive tasks. We're promoting your employees to AI managers. They become 10x more productive by overseeing AI that does the work, while they maintain quality control, strategic oversight, and final approval. Your accountant doesn't just stop doing data entry - they manage an AI that processes 10x the transactions while they ensure accuracy and make strategic decisions. Your support tech doesn't just skip password resets - they orchestrate AI handling dozens of tickets simultaneously while they tackle the complex cases. We're not replacing workers; we're multiplying their impact."
  },
  {
    question: "How long until we see real results?",
    answer: "Most clients see measurable improvements within 30 days. We start with quick wins - automating one painful process that everyone hates. Full transformation takes 3-6 months, but you'll see progress every single week. We're not building rockets here; we're making your Tuesday less terrible."
  },
  {
    question: "Is AI too complex for our team?",
    answer: "If your team can use email, they can use our AI tools. We're not asking anyone to become data scientists. The AI works behind the scenes while your team uses familiar interfaces. Plus, we train everyone and our support actually answers the phone when you call."
  },
  {
    question: "We tried AI before and it didn't work. Why would this be different?",
    answer: "AI projects often fail because implementers are making mistakes we've already made and learned from. Sometimes that means telling you honestly that your vision is too ambitious for current technology, or that we need to build toward your dream incrementally. We've been through the hype cycles and know what actually works. We start small, prove value, iterate, and grow - not 0 to 100. Each step builds on proven success, not theoretical possibilities. Our experience helps you avoid the expensive lessons we've already learned."
  },
  {
    question: "What if the AI makes mistakes?",
    answer: "Yes, it will make mistakes - but here's what matters: it's consistent. Unlike human errors that are random and unpredictable, AI makes the same type of mistakes consistently, which means we can identify, fix, and improve them. Every day it learns and gets better. Today is literally the worst it will ever be - tomorrow it's smarter."
  },
  {
    question: "How do we know our data is safe?",
    answer: "Your data never leaves your control. We can deploy on your servers, use your security protocols, and you own everything we build. We're a Calgary company - we're not shipping your data overseas. Full transparency, full control, full stop."
  },
  {
    question: "What makes you different from big consulting firms?",
    answer: "We actually implement, not just recommend. We charge thousands, not millions. We speak business, not tech jargon. And we're local - when something needs fixing, we're a coffee meeting away, not a conference call to Toronto."
  },
  {
    question: "Can we start small?",
    answer: "That's the only way we start. Pick one problem that's driving everyone crazy. We'll fix it with AI in a few weeks. Once you see it working, we expand. No massive commitments, no enterprise transformations, just solving real problems one at a time."
  },
  {
    question: "What if our industry is 'different'?",
    answer: "Every business thinks they're unique, and they're both right and wrong. Your industry has specific needs - we get that. But repetitive tasks, data analysis, and customer service exist everywhere. We adapt our AI to your reality, not the other way around."
  },
  {
    question: "What's this really going to cost?",
    answer: "Less than hiring one additional employee, more than doing nothing. We work within your budget to deliver maximum impact. Most projects start under $50K and can start development within 90 days. We're transparent about costs - no surprise invoices or hidden fees."
  }
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FAQSection>
      <div className="container">
        <SimpleFadeIn>
          <SectionTitle>Common Concerns, Honest Answers</SectionTitle>
          <SectionSubtitle>
            No sales speak. Just straight talk about what AI can and can't do for your business.
          </SectionSubtitle>
        </SimpleFadeIn>
        
        <FAQContainer>
          {faqs.map((faq, index) => (
            <SimpleFadeIn key={index} delay={index * 50}>
              <FAQItem>
                <FAQQuestion
                  onClick={() => toggleFAQ(index)}
                  $isOpen={openIndex === index}
                  aria-expanded={openIndex === index}
                >
                  <h3>{faq.question}</h3>
                  <ChevronDownIcon />
                </FAQQuestion>
                <FAQAnswer $isOpen={openIndex === index}>
                  <p>{faq.answer}</p>
                </FAQAnswer>
              </FAQItem>
            </SimpleFadeIn>
          ))}
        </FAQContainer>
      </div>
    </FAQSection>
  );
};

export default FAQ;